import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, firstValueFrom, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ArgDef, ArgEntry, ConfigMeta, UsageResult, VarDef } from '../../../shared/models';
import { SaveErrorService } from './save-error.service';
import { argsMapOf, coerceToArgType, type ArgValueType } from '../../../shared/utils/arg-value';

interface SavedConfig {
	projectSlug: string;
	chapters: string[];
	argDefs?: ArgDef[];
	varDefs?: VarDef[];
	args: { key: string; type: string; value: unknown }[];
	vars: { key: string; type: string; value: unknown }[];
	region?: string;
}

@Injectable()
export class EditorConfigService {
	private http = inject(HttpClient);
	private saveErrorService = inject(SaveErrorService);

	readonly meta = signal<ConfigMeta | null>(null);
	readonly chapters = signal<string[]>([]);
	readonly argDefs = signal<ArgDef[]>([]);
	readonly varDefs = signal<VarDef[]>([]);
	readonly args = signal<ArgEntry[]>([]);
	readonly vars = signal<ArgEntry[]>([]);
	readonly region = signal('AWS');
	readonly saving = signal(false);
	readonly saved = signal(false);
	readonly ready = signal(false);

	private initialized = false;
	private mountedChapters: string[] = [];
	private mountedArgsJson = '{}';
	private mountedVarsJson = '{}';
	private mountedRegion = 'AWS';
	private projectSlug = '';

	/** Whether the initial config load has completed (safe to start auto-save). */
	get isInitialized(): boolean {
		return this.initialized;
	}

	load(slug: string): void {
		this.projectSlug = slug;
		this.http
			.get<ConfigMeta>(`${environment.apiUrl}/api/projects/${slug}/config-meta`)
			.subscribe({
				next: (meta) => {
					this.meta.set(meta);
					this.loadConfig(slug, meta);
				},
				error: () => {
					this.meta.set({ chapters: [], args: [], vars: [] });
					this.ready.set(true);
					setTimeout(() => { this.initialized = true; });
				},
			});
	}

	save(slug: string): Observable<{ chaptersChanged: boolean; argsChanged: boolean; varsChanged: boolean; regionChanged: boolean }> {
		this.saving.set(true);

		const body = {
			chapters: this.chapters(),
			argDefs: this.argDefs(),
			varDefs: this.varDefs(),
			args: this.args().map((a) => ({ key: a.key, type: a.type, value: a.value === '' ? null : coerceToArgType(a.value, a.type as ArgValueType) })),
			vars: this.vars().map((v) => ({ key: v.key, type: v.type, value: v.value === '' ? null : coerceToArgType(v.value, v.type as ArgValueType) })),
			region: this.region(),
		};

		return this.http
			.put(`${environment.apiUrl}/api/projects/${slug}/config`, body)
			.pipe(
				map(() => {
					this.saving.set(false);
					this.saved.set(true);
					this.saveErrorService.clear();
					setTimeout(() => this.saved.set(false), 2000);

					const argsMap = this.buildArgsMap();
					const varsMap = this.buildVarsMap();
					const chaptersChanged = JSON.stringify(this.chapters()) !== JSON.stringify(this.mountedChapters);
					const argsChanged = JSON.stringify(argsMap) !== this.mountedArgsJson;
					const varsChanged = JSON.stringify(varsMap) !== this.mountedVarsJson;
					const regionChanged = this.region() !== this.mountedRegion;

					return { chaptersChanged, argsChanged, varsChanged, regionChanged };
				}),
				catchError((err) => {
					this.saving.set(false);
					this.saveErrorService.reportError(err);
					return throwError(() => err);
				}),
			);
	}

	setArgValue(index: number, value: string): void {
		this.args.update((args) => args.map((a, i) => (i === index ? { ...a, value } : a)));
	}

	setVarValue(index: number, value: string): void {
		this.vars.update((vars) => vars.map((v, i) => (i === index ? { ...v, value } : v)));
	}

	// ── ArgDef CRUD ─────────────────────────────────────────────────────────

	addArgDef(def: ArgDef): void {
		this.argDefs.update((defs) => [...defs, def]);
		// Also add a preview value entry so the user can set a value immediately
		this.args.update((args) => [...args, { key: def.key, type: def.type, value: def.defaultValue != null ? String(def.defaultValue) : '', options: def.options }]);
	}

	updateArgDef(key: string, updated: ArgDef): void {
		this.argDefs.update((defs) => defs.map((d) => (d.key === key ? updated : d)));
		// Sync options on the preview entry
		this.args.update((args) => args.map((a) => (a.key === key ? { ...a, type: updated.type, options: updated.options } : a)));
	}

	removeArgDef(key: string): void {
		this.argDefs.update((defs) => defs.filter((d) => d.key !== key));
		this.args.update((args) => args.filter((a) => a.key !== key));
	}

	// ── VarDef CRUD ─────────────────────────────────────────────────────────

	addVarDef(def: VarDef): void {
		this.varDefs.update((defs) => [...defs, def]);
		this.vars.update((vars) => [...vars, { key: def.key, type: def.type, value: def.defaultValue != null ? String(def.defaultValue) : '' }]);
	}

	updateVarDef(key: string, updated: VarDef): void {
		this.varDefs.update((defs) => defs.map((d) => (d.key === key ? updated : d)));
		this.vars.update((vars) => vars.map((v) => (v.key === key ? { ...v, type: updated.type } : v)));
	}

	removeVarDef(key: string): void {
		this.varDefs.update((defs) => defs.filter((d) => d.key !== key));
		this.vars.update((vars) => vars.filter((v) => v.key !== key));
	}

	// ── Usage endpoints ─────────────────────────────────────────────────────

	getArgDefUsage(key: string): Promise<UsageResult> {
		return firstValueFrom(
			this.http.get<UsageResult>(`${environment.apiUrl}/api/projects/${this.projectSlug}/arg-defs/${encodeURIComponent(key)}/usage`),
		);
	}

	getVarDefUsage(key: string): Promise<UsageResult> {
		return firstValueFrom(
			this.http.get<UsageResult>(`${environment.apiUrl}/api/projects/${this.projectSlug}/var-defs/${encodeURIComponent(key)}/usage`),
		);
	}

	// ── Existing helpers ────────────────────────────────────────────────────

	buildArgsMap(): Record<string, string | number | boolean> {
		return argsMapOf(this.args());
	}

	buildVarsMap(): Record<string, string | number | boolean> {
		return argsMapOf(this.vars());
	}

	snapshotMounted(): void {
		this.mountedChapters = [...this.chapters()];
		this.mountedArgsJson = JSON.stringify(this.buildArgsMap());
		this.mountedVarsJson = JSON.stringify(this.buildVarsMap());
		this.mountedRegion = this.region();
	}

	private loadConfig(slug: string, meta: ConfigMeta): void {
		this.http
			.get<SavedConfig>(`${environment.apiUrl}/api/projects/${slug}/config`)
			.subscribe({
				next: (saved) => {
					this.chapters.set(saved.chapters ?? []);
					this.region.set(saved.region ?? 'AWS');
					this.argDefs.set(saved.argDefs ?? []);
					this.varDefs.set(saved.varDefs ?? []);

					this.args.set(
						meta.args.map((def) => {
							const savedValue = saved.args.find((a) => a.key === def.key)?.value;
							return {
								key: def.key,
								type: def.type,
								value: savedValue != null ? String(savedValue) : '',
								options: def.options ?? [],
							};
						}),
					);

					this.vars.set(
						meta.vars.map((def) => {
							const savedValue = saved.vars.find((v) => v.key === def.key)?.value;
							return {
								key: def.key,
								type: def.type,
								value: savedValue != null ? String(savedValue) : '',
								options: def.options ?? [],
							};
						}),
					);

					this.ready.set(true);
					setTimeout(() => { this.initialized = true; });
				},
				error: () => {
					this.args.set(
						meta.args.map((def) => ({
							key: def.key,
							type: def.type,
							value: '',
							options: def.options ?? [],
						})),
					);

					this.vars.set(
						meta.vars.map((def) => ({
							key: def.key,
							type: def.type,
							value: '',
							options: def.options ?? [],
						})),
					);

					this.ready.set(true);
					setTimeout(() => { this.initialized = true; });
				},
			});
	}
}

/** Convert a string form value to its typed representation for the player & backend. */
